import React, { useState, useEffect, useCallback } from 'react';
import { Landmark, ChevronDown, ChevronRight, Plus, X } from 'lucide-react';
import courtRecordsService from '../../services/courtRecordsService';

// Court Records section on the case litigation tab (feat/court-crawler + linking).
// The headline is the coverage sentence — "12 of 14 entries have documents" —
// because "Synced ✓" is a lie waiting to happen. A matter may carry several
// court case numbers (dissolution + injunction, case + appeal); each links here
// and files into this matter's staff-only Court Records/ folder. Nothing is ever
// auto-published to the client portal.

const COVERAGE_STYLES = {
  retrieved: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  unretrieved: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  no_image_available: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  restricted: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  failed: 'bg-red-500/20 text-red-300 border-red-500/30',
};
const COVERAGE_LABELS = {
  retrieved: 'retrieved', unretrieved: 'not yet retrieved',
  no_image_available: 'no image at clerk', restricted: 'restricted / sealed',
  failed: 'retrieval failed',
};
const Badge = ({ state }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs border ${COVERAGE_STYLES[state] || COVERAGE_STYLES.unretrieved}`}>
    {COVERAGE_LABELS[state] || state}
  </span>
);

const CourtRecordsPanel = ({ caseId }) => {
  const [coverage, setCoverage] = useState(null);
  const [linked, setLinked] = useState([]);
  const [docket, setDocket] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [newNumber, setNewNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [vis, setVis] = useState(null); // {enabled, can_enable, reason}
  const [visBusy, setVisBusy] = useState(false);

  const load = useCallback(async () => {
    if (!caseId) return;
    try {
      setLoading(true);
      courtRecordsService.getVisibility(caseId).then(setVis).catch(() => {});
      const [cov, nums] = await Promise.all([
        courtRecordsService.getCoverage(caseId),
        courtRecordsService.listCaseNumbers(caseId),
      ]);
      setCoverage(cov);
      setLinked(nums);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load court records.');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newNumber.trim()) return;
    setBusy(true);
    try {
      const res = await courtRecordsService.addCaseNumbers(caseId, [newNumber]);
      setNewNumber('');
      if (res.conflicts && res.conflicts.length) {
        setError(`${res.conflicts.map(c => c.case_number).join(', ')} already linked to another matter.`);
      } else {
        setError(null);
      }
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add case number.');
    } finally {
      setBusy(false);
    }
  };

  const unlink = async (id, num) => {
    if (!window.confirm(`Unlink ${num} from this matter? Filed documents stay; it just stops auto-filing.`)) return;
    try {
      await courtRecordsService.unlinkCaseNumber(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to unlink.');
    }
  };

  const toggleVisibility = async () => {
    const enabling = !(vis && vis.enabled);
    if (enabling && !window.confirm(
      'Confirm: make this case\'s court records visible to the client in their portal? ' +
      'You are certifying nothing here is confidential or sealed. This is a per-case decision.')) return;
    setVisBusy(true);
    try {
      await courtRecordsService.setVisibility(caseId, enabling);
      setVis(await courtRecordsService.getVisibility(caseId));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update visibility.');
    } finally { setVisBusy(false); }
  };

  const toggleDocket = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !docket) {
      try { setDocket(await courtRecordsService.getDocket(caseId)); }
      catch (err) { setError(err.response?.data?.detail || 'Failed to load docket.'); }
    }
  };

  if (loading) return null;

  const covByNumber = {};
  (coverage?.identifiers || []).forEach((ci) => {
    covByNumber[ci.identifier.case_number] = ci;
  });

  return (
    <div className="border border-virgo-teal-700/30 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Landmark className="w-5 h-5 text-cyan-400" />
          <h4 className="text-lg font-semibold text-white">Court Records</h4>
        </div>
        {linked.length > 0 && (
          <button onClick={toggleDocket}
            className="flex items-center gap-1 text-sm text-cyan-300 hover:text-cyan-200">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Docket
          </button>
        )}
      </div>

      {error && (
        <div className="mt-2 bg-red-900/30 border border-red-600/50 rounded p-2">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* A4 — per-case client auto-visibility (attorney confirmation). */}
      {vis && (
        <div className="mt-3 flex items-start gap-3 p-2 rounded border border-virgo-teal-700/20">
          <button
            onClick={toggleVisibility}
            disabled={visBusy || (!vis.enabled && !vis.can_enable)}
            title={!vis.can_enable && !vis.enabled ? vis.reason : ''}
            className={`px-3 py-1.5 rounded text-sm ${vis.enabled
              ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
              : vis.can_enable ? 'bg-cyan-600 hover:bg-cyan-500 text-white'
                : 'bg-virgo-navy-700 text-virgo-navy-300 cursor-not-allowed'}`}>
            {vis.enabled ? 'Court records: visible to client ✓' : 'Make court records client-visible'}
          </button>
          <p className="text-xs text-virgo-navy-200 flex-1">
            {vis.enabled
              ? 'The client sees this case\'s court records automatically (read-time, fail-closed). Tap to turn off.'
              : (vis.can_enable
                ? 'Off. Only you can share these; individual docs can still be shared deliberately.'
                : vis.reason)}
          </p>
        </div>
      )}

      {/* Add a court case number (supports several, comma or newline separated) */}
      <div className="mt-3 flex gap-2">
        <input
          value={newNumber}
          onChange={(e) => setNewNumber(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="Add court case number (e.g. 45-2026-CF-000289)"
          className="flex-1 px-3 py-1.5 bg-virgo-navy-900 border border-virgo-teal-700/40 rounded text-sm text-white"
        />
        <button onClick={add} disabled={busy}
          className="flex items-center gap-1 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 rounded text-sm text-white">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      {linked.length === 0 && (
        <p className="text-sm text-virgo-navy-200 mt-3">
          No court case numbers linked to this matter yet. Add one above (or pull the
          case on the portal and it links here) and its filings land in this matter's
          staff-only Court Records folder.
        </p>
      )}

      {linked.map((ln) => {
        const ci = covByNumber[ln.case_number];
        return (
          <div key={ln.id} className="mt-3 flex flex-wrap items-center gap-3">
            <span className="text-white font-mono text-sm">{ln.case_number}</span>
            {ci ? (
              <>
                <span className="px-2 py-0.5 bg-cyan-500/20 rounded-full text-xs text-cyan-300 border border-cyan-500/30">
                  {ci.entries_with_documents} of {ci.entries_expecting_documents} entries have documents
                </span>
                {ci.total_entries !== ci.entries_expecting_documents && (
                  <span className="text-xs text-virgo-navy-200">
                    ({ci.total_entries - ci.entries_expecting_documents} no image at clerk)
                  </span>
                )}
                {ci.coverage.restricted > 0 && <Badge state="restricted" />}
                {ci.coverage.failed > 0 && <Badge state="failed" />}
              </>
            ) : (
              <span className="text-xs text-virgo-navy-200">not pulled yet</span>
            )}
            <button onClick={() => unlink(ln.id, ln.case_number)}
              title="Unlink from this matter"
              className="text-virgo-navy-300 hover:text-red-400">
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}

      <p className="text-xs text-virgo-navy-300 mt-3">
        Retrieved filings are in this matter's staff-only "Court Records" folder
        (Documents tab) — never shared to the client portal automatically.
      </p>

      {expanded && docket && (docket.dockets || []).map((d) => (
        <div key={d.case_number} className="mt-4 overflow-x-auto">
          <div className="text-xs text-virgo-navy-200 mb-1 font-mono">{d.case_number}</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-virgo-navy-200 border-b border-virgo-teal-700/30">
                <th className="py-1 pr-3">#</th>
                <th className="py-1 pr-3">Filed</th>
                <th className="py-1 pr-3">Entry</th>
                <th className="py-1 pr-3">Docs</th>
                <th className="py-1">Coverage</th>
              </tr>
            </thead>
            <tbody>
              {d.entries.map((e) => (
                <tr key={e.seq} className="border-b border-virgo-teal-700/10 text-virgo-navy-100">
                  <td className="py-1 pr-3 font-mono">{e.seq}</td>
                  <td className="py-1 pr-3 whitespace-nowrap">{e.filed_date || '—'}</td>
                  <td className="py-1 pr-3">{e.entry_text}</td>
                  <td className="py-1 pr-3">{e.doc_count}</td>
                  <td className="py-1"><Badge state={e.coverage} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default CourtRecordsPanel;
